import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, skip, switchMap } from 'rxjs/operators';
import { AuthService } from 'auth';
import { I18nService } from 'i18n';
import { CommunityService } from '../../../services/community.service';
import { CommunityAdminService } from '../../../services/community-admin.service';
import { CommunityModerationService } from '../../../services/community-moderation.service';
import { CommunityAdminUser } from '../../../models/community-admin.models';
import {
  COMMUNITY_REPORT_STATUSES,
  COMMUNITY_SANCTION_KINDS,
  CommunityModerationAction,
  CommunityModerationKind,
  CommunityModerationScopeType,
  CommunityQueuePost,
  CommunityQueueThread,
  CommunityReport,
  CommunityReportStatus,
  CommunitySanctionKind,
  moderationUserName
} from '../../../models/community-moderation.models';
import {
  CommunityChannel, CommunityGroup, CommunityOverview, communityErrorKey, communityHtmlToText,
} from '../../../models/community.models';
import { CommunityReasonModalComponent } from '../../../components/community-reason-modal/community-reason-modal.component';
import { CommunityMemberSummaryComponent } from '../../../components/community-member-summary/community-member-summary.component';

type ModerationTab = 'reports' | 'queue' | 'protocol' | 'sanction';
const TABS: readonly ModerationTab[] = ['reports', 'queue', 'protocol', 'sanction'];

/** A channel with the group it belongs to, for the scope/filter selects. */
interface ScopedChannel {
  group: CommunityGroup;
  channel: CommunityChannel;
}

/** The "resolve / dismiss" dialog: which report, which outcome. */
interface CloseReportTarget {
  report: CommunityReport;
  status: 'resolved' | 'dismissed';
}

/** The "reject" dialog of the queue. */
interface RejectTarget {
  type: 'thread' | 'post';
  id: number;
}

interface SanctionDraft {
  user: CommunityAdminUser | null;
  user_id: string;
  kind: CommunitySanctionKind;
  scope: 'network' | 'group' | 'channel';
  scope_id: number | null;
  reason: string;
  /** `datetime-local` value; sent as ISO. */
  until: string;
}

/**
 * The moderation page (Phase 2, §18–§23): reports, the approval queue, the
 * sanction protocol and the "sanction a member" form — everything scoped by
 * the backend to the channels the viewer moderates.
 *
 * Reachable from the shell when the overview says `can_moderate`. Everyone
 * else gets 403 from the first request and sees "not permitted".
 */
@Component({
  selector: 'app-community-moderation-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, TranslocoModule, CommunityReasonModalComponent, CommunityMemberSummaryComponent],
  templateUrl: './community-moderation-page.component.html',
  styleUrl: './community-moderation-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityModerationPageComponent implements OnInit, OnDestroy {
  private community = inject(CommunityService);
  private moderation = inject(CommunityModerationService);
  private admin = inject(CommunityAdminService);
  private auth = inject(AuthService);
  private i18n = inject(I18nService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  readonly sanctionKinds = COMMUNITY_SANCTION_KINDS;
  readonly reportStatuses = COMMUNITY_REPORT_STATUSES;
  readonly userName = moderationUserName;
  /** Kinds offered by the protocol filter (sanctions, lifts and warnings). */
  readonly protocolKinds: CommunityModerationKind[] = ['warn', 'mute', 'ban', 'hide', 'unmute', 'unban', 'unhide'];

  // --- Page ------------------------------------------------------------------
  overview = signal<CommunityOverview | null>(null);
  loading = signal(true);
  /** 403 on the first moderation request: the viewer moderates nothing. */
  forbidden = signal(false);
  error = signal<string | null>(null);
  busy = signal(false);
  tab = signal<ModerationTab>('reports');

  isAdmin = computed(() => this.overview()?.is_admin === true);
  /** Channels the viewer moderates, with their group (filters and scopes). */
  moderatedChannels = computed<ScopedChannel[]>(() =>
    (this.overview()?.groups ?? [])
      .filter(g => !g.locked)
      .flatMap(g => g.channels.filter(c => c.permissions.moderate).map(channel => ({ group: g, channel })))
  );
  /** Groups whose every channel the viewer moderates — the backend's rule for a group-scoped action. */
  moderatedGroups = computed<CommunityGroup[]>(() =>
    (this.overview()?.groups ?? []).filter(g => !g.locked && g.channels.length > 0 && g.channels.every(c => c.permissions.moderate))
  );

  /** Author whose moderation summary is open. */
  summaryUserId = signal<number | null>(null);

  // --- Reports -----------------------------------------------------------------
  reports = signal<CommunityReport[]>([]);
  reportsLoading = signal(false);
  reportsPage = signal(1);
  reportsTotalPages = signal(1);
  reportsTotal = signal(0);
  openCount = signal<number | null>(null);
  /** '' = open + in review (the backend default). */
  reportStatus = signal<CommunityReportStatus | ''>('');
  reportChannelId = signal<number | null>(null);
  closeTarget = signal<CloseReportTarget | null>(null);
  closeError = signal<string | null>(null);

  // --- Queue -------------------------------------------------------------------
  queueThreads = signal<CommunityQueueThread[]>([]);
  queuePosts = signal<CommunityQueuePost[]>([]);
  queueLoading = signal(false);
  queueChannelId = signal<number | null>(null);
  /** Queue items whose full body is unfolded (`thread:<id>` / `post:<id>`). */
  expanded = signal<Set<string>>(new Set());
  rejectTarget = signal<RejectTarget | null>(null);
  rejectError = signal<string | null>(null);

  // --- Protocol ----------------------------------------------------------------
  actions = signal<CommunityModerationAction[]>([]);
  actionsLoading = signal(false);
  actionsPage = signal(1);
  actionsTotalPages = signal(1);
  actionsTotal = signal(0);
  protocolKind = signal<CommunityModerationKind | ''>('');
  protocolUserId = '';
  liftTarget = signal<CommunityModerationAction | null>(null);
  liftError = signal<string | null>(null);

  // --- Sanction form -------------------------------------------------------------
  sanctionDraft: SanctionDraft = this.emptySanctionDraft();
  sanctionError = signal<string | null>(null);
  sanctionErrorDetail = signal<string | null>(null);
  /** The action just created, for the confirmation line. */
  sanctionCreated = signal<CommunityModerationAction | null>(null);
  userResults = signal<CommunityAdminUser[]>([]);
  userSearchTerm = '';
  private userSearch$ = new Subject<string>();
  private langSub?: Subscription;
  /**
   * `?tab=sanction&user=<id>[&kind=mute|warn|ban]` — the admin user list links
   * here to sanction one member. Applied once, after the overview told us
   * whether the viewer is an admin (which decides the default scope).
   */
  private sanctionPrefill: { userId: number; kind: CommunitySanctionKind | null } | null = null;

  ngOnInit(): void {
    const requested = this.route.snapshot.queryParamMap.get('tab') as ModerationTab | null;
    if (requested && TABS.includes(requested)) this.tab.set(requested);
    const user = this.route.snapshot.queryParamMap.get('user');
    if (user && /^\d+$/.test(user)) {
      this.protocolUserId = user;
      if (this.tab() === 'sanction') {
        const kind = this.route.snapshot.queryParamMap.get('kind') as CommunitySanctionKind | null;
        const prefillKinds: readonly CommunitySanctionKind[] = ['mute', 'warn', 'ban'];
        this.sanctionPrefill = { userId: Number(user), kind: kind && prefillKinds.includes(kind) ? kind : null };
      }
    }

    this.userSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(q =>
          q.trim().length < 2
            ? of({ data: [] as CommunityAdminUser[] })
            : this.admin.searchUsers(q.trim()).pipe(catchError(() => of({ data: [] as CommunityAdminUser[] })))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(res => this.userResults.set(res.data));

    this.loadOverview();
    // Channel and group names come back in the UI language.
    this.langSub = this.i18n.currentLanguage$.pipe(skip(1)).subscribe(() => this.loadOverview(false));
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  get lang(): string {
    return this.i18n.getLanguage();
  }

  get myUserId(): number | null {
    return this.auth.getUser()?.id ?? null;
  }

  communityLink(): string {
    return `/${this.lang}/community`;
  }

  threadLink(threadId: number | null | undefined): string | null {
    return threadId ? `/${this.lang}/community/${threadId}` : null;
  }

  memberLink(userId: number | null | undefined): string | null {
    return userId ? `/${this.lang}/community/u/${userId}` : null;
  }

  // --- Loading -------------------------------------------------------------------

  /**
   * The overview feeds the filters (channels, groups, admin flag); the tab's own
   * list is loaded right after. The first moderation request decides whether
   * the viewer belongs here at all.
   */
  private loadOverview(loadTab = true): void {
    this.community.getOverview(this.lang).subscribe({
      next: res => {
        this.overview.set(res);
        if (res.can_moderate === false) {
          this.loading.set(false);
          this.forbidden.set(true);
          return;
        }
        if (loadTab) this.loadTab(this.tab());
        this.applySanctionPrefill();
      },
      error: err => {
        this.loading.set(false);
        this.error.set(communityErrorKey(err));
      }
    });
  }

  selectTab(tab: ModerationTab): void {
    this.tab.set(tab);
    this.clearError();
    this.router.navigate([], { relativeTo: this.route, queryParams: { tab }, queryParamsHandling: 'merge', replaceUrl: true });
    this.loadTab(tab);
  }

  private loadTab(tab: ModerationTab): void {
    switch (tab) {
      case 'reports': this.loadReports(1); break;
      case 'queue': this.loadQueue(); break;
      case 'protocol': this.loadActions(1); break;
      case 'sanction': this.loading.set(false); break;
    }
  }

  clearError(): void {
    this.error.set(null);
  }

  /** A 403 anywhere means "not a moderator"; everything else is shown inline. */
  private fail(err: unknown): void {
    this.loading.set(false);
    this.busy.set(false);
    if ((err as { status?: number })?.status === 403) {
      this.forbidden.set(true);
      return;
    }
    this.error.set(communityErrorKey(err));
  }

  private run(request: Observable<unknown>, after: () => void): void {
    this.clearError();
    this.busy.set(true);
    request.subscribe({
      next: () => {
        this.busy.set(false);
        after();
      },
      error: err => this.fail(err)
    });
  }

  // ===========================================================================
  // Reports
  // ===========================================================================

  loadReports(page: number): void {
    this.reportsLoading.set(true);
    this.moderation.listReports({ status: this.reportStatus(), channelId: this.reportChannelId(), page, perPage: 25 }).subscribe({
      next: res => {
        this.reports.set(res.data);
        this.reportsPage.set(res.page);
        this.reportsTotalPages.set(res.total_pages);
        this.reportsTotal.set(res.total_count);
        this.openCount.set(res.open_count);
        this.reportsLoading.set(false);
        this.loading.set(false);
        this.forbidden.set(false);
      },
      error: err => {
        this.reportsLoading.set(false);
        this.fail(err);
      }
    });
  }

  onReportStatusChange(value: string): void {
    this.reportStatus.set(value as CommunityReportStatus | '');
    this.loadReports(1);
  }

  onReportChannelChange(value: string | number | null): void {
    this.reportChannelId.set(value == null || value === '' ? null : Number(value));
    this.loadReports(1);
  }

  /** Takes the report into review under the viewer's name (also re-assigns one held by somebody else). */
  assignToMe(report: CommunityReport): void {
    const payload = this.myUserId ? { status: 'in_review' as const, assignee_id: this.myUserId } : { status: 'in_review' as const };
    this.run(this.moderation.updateReport(report.id, payload), () => this.loadReports(this.reportsPage()));
  }

  openClose(report: CommunityReport, status: 'resolved' | 'dismissed'): void {
    this.closeError.set(null);
    this.closeTarget.set({ report, status });
  }

  /** Closing needs the written outcome (`resolution`), which the backend validates. */
  confirmClose(resolution: string): void {
    const target = this.closeTarget();
    if (!target) return;
    this.busy.set(true);
    this.closeError.set(null);
    this.moderation.updateReport(target.report.id, { status: target.status, resolution }).subscribe({
      next: () => {
        this.busy.set(false);
        this.closeTarget.set(null);
        this.loadReports(this.reportsPage());
      },
      error: err => {
        this.busy.set(false);
        this.closeError.set(communityErrorKey(err));
      }
    });
  }

  reportStatusClass(status: CommunityReportStatus): string {
    switch (status) {
      case 'open': return 'bg-danger';
      case 'in_review': return 'bg-warning text-dark';
      case 'resolved': return 'bg-success';
      default: return 'bg-secondary';
    }
  }

  isMine(report: CommunityReport): boolean {
    return !!report.assignee && report.assignee.id === this.myUserId;
  }

  // ===========================================================================
  // Queue
  // ===========================================================================

  loadQueue(): void {
    this.queueLoading.set(true);
    this.moderation.getQueue(this.queueChannelId()).subscribe({
      next: res => {
        this.queueThreads.set(res.threads);
        this.queuePosts.set(res.posts);
        this.queueLoading.set(false);
        this.loading.set(false);
        this.forbidden.set(false);
      },
      error: err => {
        this.queueLoading.set(false);
        this.fail(err);
      }
    });
  }

  onQueueChannelChange(value: string | number | null): void {
    this.queueChannelId.set(value == null || value === '' ? null : Number(value));
    this.loadQueue();
  }

  queueCount(): number {
    return this.queueThreads().length + this.queuePosts().length;
  }

  approve(type: 'thread' | 'post', id: number): void {
    this.run(this.moderation.decide(type, id, true), () => this.loadQueue());
  }

  openReject(type: 'thread' | 'post', id: number): void {
    this.rejectError.set(null);
    this.rejectTarget.set({ type, id });
  }

  confirmReject(reason: string): void {
    const target = this.rejectTarget();
    if (!target) return;
    this.busy.set(true);
    this.rejectError.set(null);
    this.moderation.decide(target.type, target.id, false, reason).subscribe({
      next: () => {
        this.busy.set(false);
        this.rejectTarget.set(null);
        this.loadQueue();
      },
      error: err => {
        this.busy.set(false);
        this.rejectError.set(communityErrorKey(err));
      }
    });
  }

  toggleExpanded(key: string): void {
    this.expanded.update(set => {
      const next = new Set(set);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  /** Plain-text preview of a body; the full HTML is never rendered on this page. */
  preview(html: string | null | undefined, max = 300): string {
    const text = communityHtmlToText(html);
    return text.length > max ? `${text.slice(0, max)}…` : text;
  }

  // ===========================================================================
  // Protocol
  // ===========================================================================

  loadActions(page: number): void {
    const userId = /^\d+$/.test(this.protocolUserId.trim()) ? Number(this.protocolUserId.trim()) : null;
    this.actionsLoading.set(true);
    this.moderation.listActions({ kind: this.protocolKind(), userId, page, perPage: 25 }).subscribe({
      next: res => {
        this.actions.set(res.data);
        this.actionsPage.set(res.page);
        this.actionsTotalPages.set(res.total_pages);
        this.actionsTotal.set(res.total_count);
        this.actionsLoading.set(false);
        this.loading.set(false);
        this.forbidden.set(false);
      },
      error: err => {
        this.actionsLoading.set(false);
        this.fail(err);
      }
    });
  }

  onProtocolKindChange(value: string): void {
    this.protocolKind.set(value as CommunityModerationKind | '');
    this.loadActions(1);
  }

  onProtocolUserChange(value: string): void {
    this.protocolUserId = value;
    if (value.trim() === '' || /^\d+$/.test(value.trim())) this.loadActions(1);
  }

  /** Only a sanction that is neither lifted nor expired can be lifted. */
  canLift(a: CommunityModerationAction): boolean {
    return (a.kind === 'mute' || a.kind === 'ban' || a.kind === 'hide') && !a.lifted && !a.expired;
  }

  actionState(a: CommunityModerationAction): 'active' | 'lifted' | 'expired' | null {
    if (a.lifted == null && a.expired == null) return null;
    if (a.lifted) return 'lifted';
    if (a.expired) return 'expired';
    return 'active';
  }

  openLift(action: CommunityModerationAction): void {
    this.liftError.set(null);
    this.liftTarget.set(action);
  }

  confirmLift(reason: string): void {
    const target = this.liftTarget();
    if (!target) return;
    this.busy.set(true);
    this.liftError.set(null);
    this.moderation.liftAction(target.id, reason).subscribe({
      next: () => {
        this.busy.set(false);
        this.liftTarget.set(null);
        this.loadActions(this.actionsPage());
      },
      error: err => {
        this.busy.set(false);
        this.liftError.set(communityErrorKey(err));
      }
    });
  }

  kindClass(kind: string): string {
    switch (kind) {
      case 'ban': return 'bg-danger';
      case 'hide': return 'bg-dark';
      case 'mute': return 'bg-warning text-dark';
      case 'warn': return 'bg-info text-dark';
      default: return 'bg-success';
    }
  }

  openSummary(userId: number | null | undefined): void {
    if (userId) this.summaryUserId.set(userId);
  }

  /** After a lift from the summary dialog the protocol is stale. */
  onSummaryChanged(): void {
    if (this.tab() === 'protocol') this.loadActions(this.actionsPage());
  }

  // ===========================================================================
  // Sanction a member
  // ===========================================================================

  private emptySanctionDraft(): SanctionDraft {
    return { user: null, user_id: '', kind: 'warn', scope: 'channel', scope_id: null, reason: '', until: '' };
  }

  resetSanctionForm(): void {
    this.sanctionDraft = this.emptySanctionDraft();
    this.userSearchTerm = '';
    this.userResults.set([]);
    this.sanctionError.set(null);
    this.sanctionErrorDetail.set(null);
    // Default scope: network for admins, else the first moderated channel.
    if (this.isAdmin()) {
      this.sanctionDraft.scope = 'network';
    } else {
      this.sanctionDraft.scope_id = this.moderatedChannels()[0]?.channel.id ?? null;
    }
  }

  onUserSearch(term: string): void {
    this.userSearchTerm = term;
    this.sanctionDraft.user = null;
    this.userSearch$.next(term);
  }

  pickUser(user: CommunityAdminUser): void {
    this.sanctionDraft.user = user;
    this.sanctionDraft.user_id = String(user.id);
    this.userSearchTerm = this.userLabel(user);
    this.userResults.set([]);
  }

  userLabel(user: CommunityAdminUser | null | undefined): string {
    if (!user) return '—';
    const name = user.full_name || user.username || '';
    return name ? `${name} (${user.email})` : user.email;
  }

  /** `hide` is always network-wide (and therefore admins only). */
  onKindChange(kind: CommunitySanctionKind): void {
    this.sanctionDraft.kind = kind;
    if (kind === 'hide') this.onScopeChange('network');
  }

  onScopeChange(scope: 'network' | 'group' | 'channel'): void {
    this.sanctionDraft.scope = scope;
    this.sanctionDraft.scope_id = scope === 'group'
      ? this.moderatedGroups()[0]?.id ?? null
      : scope === 'channel' ? this.moderatedChannels()[0]?.channel.id ?? null : null;
  }

  sanctionDraftValid(): boolean {
    const d = this.sanctionDraft;
    const userId = d.user?.id ?? Number(d.user_id);
    if (!userId || Number.isNaN(userId) || userId <= 0) return false;
    if (!d.reason.trim()) return false;
    if (d.scope === 'network' && !this.isAdmin()) return false;
    if (d.scope !== 'network' && d.scope_id == null) return false;
    return true;
  }

  submitSanction(): void {
    if (!this.sanctionDraftValid()) return;
    const d = this.sanctionDraft;
    const scopeType: CommunityModerationScopeType | null =
      d.scope === 'group' ? 'CommunityGroup' : d.scope === 'channel' ? 'CommunityChannel' : null;
    let until: string | null = null;
    if (d.until) {
      const date = new Date(d.until);
      until = Number.isNaN(date.getTime()) ? null : date.toISOString();
    }
    this.sanctionError.set(null);
    this.sanctionErrorDetail.set(null);
    this.sanctionCreated.set(null);
    this.busy.set(true);
    this.moderation.createAction({
      user_id: d.user?.id ?? Number(d.user_id),
      kind: d.kind,
      scope_type: scopeType,
      scope_id: scopeType ? d.scope_id : null,
      reason: d.reason.trim(),
      until
    }).subscribe({
      next: res => {
        this.busy.set(false);
        this.sanctionCreated.set(res.action);
        this.resetSanctionForm();
      },
      error: err => {
        this.busy.set(false);
        if (err?.status === 403) {
          this.sanctionError.set('community.errors.community_forbidden');
          return;
        }
        this.sanctionError.set(communityErrorKey(err));
        const errors = err?.error?.errors;
        if (errors && typeof errors === 'object') {
          this.sanctionErrorDetail.set(
            Object.entries(errors as Record<string, unknown>)
              .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : String(msgs)}`)
              .join(' · ')
          );
        }
      }
    });
  }

  /**
   * Pre-fills the sanction form from the query string (see `sanctionPrefill`).
   * An admin also gets the member resolved by id, so the form names them;
   * a moderator (who may not search users) sees the id in the manual field.
   */
  private applySanctionPrefill(): void {
    const prefill = this.sanctionPrefill;
    if (!prefill) return;
    this.sanctionPrefill = null;
    this.resetSanctionForm();
    this.sanctionDraft.user_id = String(prefill.userId);
    if (prefill.kind) this.onKindChange(prefill.kind);
    if (!this.isAdmin()) return;
    this.userSearchTerm = `#${prefill.userId}`;
    // The search needs two characters; a one-digit id stays in user_id only.
    if (String(prefill.userId).length < 2) return;
    this.admin.searchUsers(String(prefill.userId)).pipe(catchError(() => of({ data: [] as CommunityAdminUser[] }))).subscribe(res => {
      const match = res.data.find(u => u.id === prefill.userId);
      if (match && this.sanctionDraft.user_id === String(prefill.userId)) this.pickUser(match);
    });
  }

  /** Jump to the protocol filtered on one member. */
  showProtocolFor(userId: number): void {
    this.protocolUserId = String(userId);
    this.selectTab('protocol');
  }
}
